import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, MenuItem, Stack, TextField, Typography
} from '@mui/material';
import { Search as SearchIcon, X as ClearIcon } from 'react-feather';
import { TRANSACTION_STYLE_COLORS } from 'src/components/transactions/transactionStyleTokens';

const ColumnSearch = ({
  fields, values = {}, onApply, onClear, sx = {}, containerSx = {}
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({});
  const [draft, setDraft] = useState({});
  const [error, setError] = useState('');

  const resetDraft = () => {
    setSelected(fields.reduce((result, field) => ({
      ...result, [field.key]: Boolean(field.defaultChecked || values[field.key])
    }), {}));
    setDraft(fields.reduce((result, field) => ({
      ...result, [field.key]: values[field.key] || ''
    }), {}));
    setError('');
  };

  useEffect(() => { resetDraft(); }, [fields, values]);

  const apply = () => {
    const result = fields.reduce((output, field) => {
      const value = selected[field.key] ? String(draft[field.key] || '').trim() : '';
      return value ? { ...output, [field.key]: value } : output;
    }, {});
    if (!Object.keys(result).length) {
      setError('Enter a value in at least one selected column.');
      return;
    }
    onApply(result);
    setOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          width: { md: 'auto', xs: '100%' },
          minWidth: 0,
          ...containerSx
        }}
      >
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'
        }}
        >
          <Button
            variant="outlined"
            startIcon={<SearchIcon size={16} />}
            onClick={() => { resetDraft(); setOpen(true); }}
            sx={{
              height: 40,
              width: { md: 320, xs: '100%' },
              justifyContent: 'flex-start',
              textTransform: 'none',
              borderRadius: 2,
              color: TRANSACTION_STYLE_COLORS.primary,
              borderColor: TRANSACTION_STYLE_COLORS.primary,
              backgroundColor: '#fff',
              ...sx
            }}
          >
            Search
          </Button>
        </Box>
        {Object.keys(values).length > 0 && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'nowrap',
            width: 'max-content',
            whiteSpace: 'nowrap',
            mt: 1
          }}
          >
            <Typography
              variant="body2"
              sx={{
                color: TRANSACTION_STYLE_COLORS.foreground,
                fontWeight: 600,
                lineHeight: '28px'
              }}
            >
              Search Filters:
            </Typography>
            {Object.entries(values).map(([key, value]) => {
              const field = fields.find((item) => item.key === key);
              if (!field || !value) return null;
              const nextValues = { ...values };
              delete nextValues[key];
              return (
                <Chip
                  key={key}
                  label={`${field.label}: ${value}`}
                  size="small"
                  onDelete={() => (Object.keys(nextValues).length ? onApply(nextValues) : onClear())}
                  sx={{ color: TRANSACTION_STYLE_COLORS.foreground, backgroundColor: TRANSACTION_STYLE_COLORS.selected }}
                />
              );
            })}
            <Button size="small" onClick={onClear} sx={{ color: TRANSACTION_STYLE_COLORS.primary, textTransform: 'none' }}>
              Clear all
            </Button>
          </Box>
        )}
      </Box>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Search by columns</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: TRANSACTION_STYLE_COLORS.muted, mb: 2 }}>
            Select one or more columns and enter values. All entered values must match.
          </Typography>
          <Stack spacing={1.5}>
            {fields.map((field) => (
              <Box
                key={field.key}
                sx={{
                  display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(170px, .8fr) minmax(0, 1fr)' }, alignItems: 'center', gap: 1
                }}
              >
                <FormControlLabel
                  control={<Checkbox checked={Boolean(selected[field.key])} onChange={() => setSelected((current) => ({ ...current, [field.key]: !current[field.key] }))} />}
                  label={field.label}
                />
                <TextField
                  select={field.type === 'select'}
                  size="small"
                  fullWidth
                  disabled={!selected[field.key]}
                  value={draft[field.key] || ''}
                  placeholder={`Search ${field.label.toLowerCase()}`}
                  onChange={(event) => setDraft((current) => ({ ...current, [field.key]: event.target.value }))}
                  onKeyDown={(event) => { if (event.key === 'Enter') apply(); }}
                  InputProps={field.type === 'select' || !draft[field.key] ? undefined : { endAdornment: <IconButton size="small" onClick={() => setDraft((current) => ({ ...current, [field.key]: '' }))}><ClearIcon size={15} /></IconButton> }}
                >
                  {field.type === 'select' && [
                    <MenuItem value="" key="empty">{`Select ${field.label.toLowerCase()}`}</MenuItem>,
                    ...(field.options || []).map((option) => (
                      <MenuItem value={option.id} key={option.id}>{option.label}</MenuItem>
                    ))
                  ]}
                </TextField>
              </Box>
            ))}
          </Stack>
          {error && <Typography role="alert" sx={{ color: '#b42318', mt: 2 }}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { onClear(); setOpen(false); }}>Reset all</Button>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={apply}>Search</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ColumnSearch.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['text', 'select']),
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    }))
  })).isRequired,
  values: PropTypes.object,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  sx: PropTypes.object,
  containerSx: PropTypes.object
};

export default ColumnSearch;
